import styles from './form.module.scss';
import { useState } from 'react';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import api from '@/api/intercepted';
import { useTranslation } from 'next-i18next';
import * as Sentry from "@sentry/nextjs";
import useFederalDistricts from '@/components/common/useFederalDistricts';

const SALES_CHANNELS = ['HoReCa', 'Розница', 'Опт'];

const variants = {
  initial: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 1.3,
      delay: 1,
    },
  },
  hidden: { opacity: 0 },
};

const RequestPriceForm = ({ requestData, onSuccess, onClose }) => {
  const [errors, setErrors] = useState(false);
  const [success, setSuccess] = useState(false);
  const { register, handleSubmit, reset, formState: { errors: formErrors } } = useForm();
  const { t } = useTranslation(['common', 'products']);
  const federalDistricts = useFederalDistricts();

  const onSubmit = async data => {
    setErrors(false);
    setSuccess(false);
    // console.log(data, myFiles);
    const formData = new FormData();

    data.phone = data.phone.replace(/[^\d+]/g,'')

    Object.entries({ ...requestData, ...data }).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const res = await api.post('/v1/api/products/request/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status === 201) {
        resetForm();
        setSuccess(true);
        onSuccess();
      }
    } catch (error) {
      Sentry.captureException(error);
      setErrors(true);
    }
  };

  const resetForm = () => {
    reset({
      name: '',
      phone: '',
      email: '',
      sales_channel: '',
      federal_district: '',
      city: '',
      agreement: false
    });
  };

  return (
    <motion.form
      className={styles.wrapper}
      onSubmit={handleSubmit(onSubmit)}
      initial="initial"
      animate="visible"
      exit="hidden"
      variants={variants}
    >
      <svg
        className={styles.closeButton}
        onClick={onClose}
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10.5001 0L5.99947 4.5L1.49987 0L0 1.5L4.4996 6L0 10.5L1.49987 12L5.99947 7.5L10.5001 12L12 10.5L7.5004 6L12 1.5L10.5001 0Z"
          fill="white"
        />
      </svg>

      {errors && (
        <div className={clsx(styles.formLine, styles.formLineErrors)}>
          {t(
            'Произошла ошибка при отправке формы. Пожалуйста, проверьте правильно ли Вы заполнили все поля.'
          )}
        </div>
      )}
      {success && (
        <div className={clsx(styles.formLine, styles.formLineSuccess)}>
          {t('Заявка успешно отправлена.')}
        </div>
      )}
      <div className={clsx(styles.formLine, formErrors.phone && styles.error)}>
        <input
          type="text"
          placeholder={t('Телефон')}
          {...register('phone', {
            required: true,
            maxLength: 256,
            onChange: (event) => {
              const {value} = event.target;
              event.target.value = value.replace(/[^\d\(\)\-—+\s]/g, '');
            }
          })}
        />
      </div>
      <div className={clsx(styles.formLine, formErrors.email && styles.error)}>
        <input
          type="email"
          placeholder={t('E-mail')}
          {...register('email', { required: true, maxLength: 256 })}
        />
      </div>
      <div className={clsx(styles.formLine, formErrors.name && styles.error)}>
        <input
          type="text"
          placeholder={t('Имя')}
          {...register('name', { required: true, maxLength: 256 })}
        />
      </div>
      <div className={clsx(styles.formLine, formErrors.sales_channel && styles.error)}>
        <input
          type="text"
          list="category-request-price-sales-channel"
          placeholder={t('Канал продаж')}
          {...register('sales_channel', {
            required: true,
            validate: value => SALES_CHANNELS.includes(value)
          })}
        />
        <datalist id="category-request-price-sales-channel">
          {SALES_CHANNELS.map(channel => (
            <option value={channel} label={t(channel)} key={channel} />
          ))}
        </datalist>
      </div>
      <div className={clsx(styles.formLine, styles.selectLine, formErrors.federal_district && styles.error)}>
        <select defaultValue="" required {...register('federal_district', { required: true })}>
          <option value="" disabled hidden>{t('Федеральный округ')}</option>
          {federalDistricts.map(district => (
            <option value={district.title} key={district.id}>{district.title}</option>
          ))}
        </select>
      </div>
      <div className={clsx(styles.formLine, formErrors.city && styles.error)}>
        <input
          type="text"
          placeholder={t('Город')}
          {...register('city', { required: true, maxLength: 256 })}
        />
      </div>
      <div className={clsx(styles.formAgreement, formErrors.agreement && styles.error)}>
        <input
            className={styles.checkbox}
            id="policy"
            type="checkbox"
            {...register('agreement', {required: true})}
        />
        <label htmlFor="policy">{t(
            'Я согласен на обработку персональных данных на условиях '
        )}<a
            href="/privacy-policy" target="_blank">{t(
            'Политики в отношении обработки персональных данных'
        )}
        </a></label>
      </div>
      <div className={styles.actions}>
        <button type="submit" className={clsx(styles.button, styles.buttonSend)}>
          {t('Получить прайс')}
        </button>
      </div>
    </motion.form>
  );
};

export default RequestPriceForm;
